// Music services connection: Spotify (PKCE OAuth), Apple Music (MusicKit), Amazon Music (deep-link)
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ExternalLink, Loader2, LogOut, Music2, Apple, ShoppingBag, Settings, Play, Headphones } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Spotify PKCE helpers (puro cliente) ────────────────────────────────────
const SPOTIFY_SCOPES = 'user-read-private user-read-email user-read-playback-state user-modify-playback-state user-read-currently-playing playlist-read-private playlist-read-collaborative streaming';
const REDIRECT = `${window.location.origin}/music/spotify-callback`;

const b64url = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
  .replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
const sha256 = async (s) => crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
const randStr = (n=64) => {
  const arr = new Uint8Array(n); crypto.getRandomValues(arr);
  return Array.from(arr, b => ('0'+b.toString(16)).slice(-2)).join('');
};

const spotifyConnect = async (clientId) => {
  if (!clientId) { toast.error('Falta SPOTIFY_CLIENT_ID en el servidor'); return; }
  const verifier = randStr(64);
  const challenge = b64url(await sha256(verifier));
  sessionStorage.setItem('spotify_pkce_verifier', verifier);
  sessionStorage.setItem('spotify_pkce_client_id', clientId);
  const params = new URLSearchParams({
    response_type: 'code', client_id: clientId, scope: SPOTIFY_SCOPES,
    code_challenge_method: 'S256', code_challenge: challenge, redirect_uri: REDIRECT,
  });
  window.location.href = `https://accounts.spotify.com/authorize?${params}`;
};

const spotifyRefresh = async () => {
  const tok = JSON.parse(localStorage.getItem('spotify_token') || 'null');
  if (!tok?.refresh_token) return null;
  const clientId = sessionStorage.getItem('spotify_pkce_client_id') || tok.client_id;
  const body = new URLSearchParams({
    grant_type: 'refresh_token', refresh_token: tok.refresh_token, client_id: clientId,
  });
  try {
    const res = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body
    });
    const data = await res.json();
    if (data.access_token) {
      const next = { ...tok, ...data, expires_at: Date.now() + data.expires_in*1000 };
      localStorage.setItem('spotify_token', JSON.stringify(next));
      return next.access_token;
    }
  } catch {}
  return null;
};

const spotifyApi = async (path) => {
  let tok = JSON.parse(localStorage.getItem('spotify_token') || 'null');
  if (!tok) return null;
  if (Date.now() > (tok.expires_at || 0)) tok = { access_token: await spotifyRefresh() };
  if (!tok?.access_token) return null;
  const res = await fetch(`https://api.spotify.com/v1${path}`, {
    headers: { Authorization: `Bearer ${tok.access_token}` }
  });
  if (!res.ok) return null;
  return res.json();
};

const MusicConnect = ({ user }) => {
  const [config, setConfig] = useState(null);
  const [spotifyMe, setSpotifyMe] = useState(null);
  const [appleStatus, setAppleStatus] = useState(null);
  const [amazonConnected, setAmazonConnected] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [showSettings, setShowSettings] = useState(null); // 'spotify'|'apple'|null
  const [customSpotifyId, setCustomSpotifyId] = useState('');

  // Carga config pública del backend
  useEffect(() => {
    axios.get(`${API}/music/services/config`).then(r => setConfig(r.data)).catch(() => setConfig({}));
    setAmazonConnected(localStorage.getItem('amazon_music_connected') === '1');
    setCustomSpotifyId(localStorage.getItem('spotify_custom_client_id') || '');
  }, []);

  // Si hay token Spotify, carga datos del usuario
  const refreshSpotify = useCallback(async () => {
    const tok = JSON.parse(localStorage.getItem('spotify_token') || 'null');
    if (!tok?.access_token) { setSpotifyMe(null); return; }
    const me = await spotifyApi('/me');
    if (me) setSpotifyMe(me);
  }, []);

  useEffect(() => { refreshSpotify(); }, [refreshSpotify]);

  // Carga MusicKit de Apple
  useEffect(() => {
    if (!config?.apple?.dev_token) return;
    if (window.MusicKit) { initApple(); return; }
    const s = document.createElement('script');
    s.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
    s.async = true;
    s.setAttribute('data-web-components', '');
    s.onload = () => {
      document.addEventListener('musickitloaded', initApple, { once: true });
      // si ya cargó, también
      if (window.MusicKit) initApple();
    };
    document.head.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const initApple = async () => {
    if (!window.MusicKit || !config?.apple?.dev_token) return;
    try {
      await window.MusicKit.configure({
        developerToken: config.apple.dev_token,
        app: { name: 'OkBot', build: '2026.1' }
      });
      const mk = window.MusicKit.getInstance();
      setAppleStatus({ authorized: !!mk.isAuthorized, instance: mk });
    } catch (e) { /* silent */ }
  };

  const appleConnect = async () => {
    if (!appleStatus?.instance) { toast.error('MusicKit no disponible. ¿Configuraste APPLE_MUSIC_DEV_TOKEN?'); return; }
    setLoadingApple(true);
    try {
      await appleStatus.instance.authorize();
      setAppleStatus({ ...appleStatus, authorized: true });
      toast.success('Apple Music conectado');
    } catch { toast.error('Cancelaste la autorización'); }
    finally { setLoadingApple(false); }
  };

  const appleDisconnect = async () => {
    try { await appleStatus?.instance?.unauthorize(); } catch {}
    setAppleStatus(s => ({ ...s, authorized: false }));
    toast.success('Apple Music desconectado');
  };

  const spotifyDisconnect = () => {
    localStorage.removeItem('spotify_token');
    setSpotifyMe(null);
    toast.success('Spotify desconectado');
  };

  const amazonToggle = () => {
    if (amazonConnected) {
      localStorage.removeItem('amazon_music_connected');
      setAmazonConnected(false);
    } else {
      localStorage.setItem('amazon_music_connected', '1');
      setAmazonConnected(true);
      toast.success('Amazon Music marcado como conectado');
    }
  };

  const saveCustomSpotify = () => {
    if (customSpotifyId.trim()) {
      localStorage.setItem('spotify_custom_client_id', customSpotifyId.trim());
      toast.success('Client ID guardado');
    } else {
      localStorage.removeItem('spotify_custom_client_id');
    }
    setShowSettings(null);
  };

  const effectiveSpotifyClientId = customSpotifyId || config?.spotify?.client_id;

  // ── UI Card ─────────────────────────────────────────────────────────────
  const ServiceCard = ({ service, name, color, icon: Icon, connected, statusText, children, actions }) => (
    <div className="bg-[#0d1117] border border-white/10 rounded-2xl p-5 flex flex-col" style={{ background: `linear-gradient(160deg, ${color}11, transparent 60%)` }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: color }}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold">{name}</h3>
          <p className="text-xs flex items-center gap-1.5 mt-0.5" style={{ color: connected ? '#22c55e' : '#888' }}>
            {connected && <CheckCircle2 size={11} />}
            {statusText}
          </p>
        </div>
        <button onClick={() => setShowSettings(service)} className="p-1.5 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg" title="Ajustes">
          <Settings size={14} />
        </button>
      </div>
      <div className="flex-1 text-sm text-gray-400 mb-4">{children}</div>
      <div className="flex gap-2">{actions}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-lg font-semibold flex items-center gap-2">
            <Headphones className="text-blue-400" size={20} /> Servicios musicales
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Conecta tus cuentas para reproducir directamente desde OkBot</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

        {/* ── SPOTIFY ────────────────────────────────────────────────── */}
        <ServiceCard
          service="spotify"
          name="Spotify"
          color="#1DB954"
          icon={Music2}
          connected={!!spotifyMe}
          statusText={spotifyMe ? `Conectado: ${spotifyMe.display_name || spotifyMe.email}` : 'No conectado'}
          actions={
            spotifyMe ? (
              <>
                <button onClick={refreshSpotify} className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-sm">Refrescar</button>
                <button onClick={spotifyDisconnect} className="px-3 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg text-sm flex items-center gap-1.5">
                  <LogOut size={13} /> Salir
                </button>
              </>
            ) : (
              <button
                onClick={() => spotifyConnect(effectiveSpotifyClientId)}
                disabled={!effectiveSpotifyClientId}
                className="w-full px-3 py-2 bg-[#1DB954] hover:bg-[#1ed760] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm flex items-center justify-center gap-1.5"
                title={!effectiveSpotifyClientId ? 'Configura un Client ID en ajustes' : 'Iniciar OAuth'}
              >
                <ExternalLink size={13} /> Conectar Spotify
              </button>
            )
          }
        >
          {spotifyMe ? (
            <div className="flex items-center gap-3">
              {spotifyMe.images?.[0]?.url && <img src={spotifyMe.images[0].url} alt="" className="w-10 h-10 rounded-full" />}
              <div className="text-xs">
                <p className="text-gray-300">{spotifyMe.product || 'free'} · {spotifyMe.country}</p>
                <p className="text-gray-500">{spotifyMe.followers?.total ?? 0} seguidores</p>
              </div>
            </div>
          ) : effectiveSpotifyClientId ? (
            <p>Reproduce, controla cola y crea playlists desde OkBot. Necesitas cuenta Spotify Premium para reproducción Web.</p>
          ) : (
            <p className="text-amber-400">⚠ Falta configurar <code className="bg-white/5 px-1 rounded">SPOTIFY_CLIENT_ID</code> en el servidor o introduce uno propio en ajustes.</p>
          )}
        </ServiceCard>

        {/* ── APPLE MUSIC ────────────────────────────────────────────── */}
        <ServiceCard
          service="apple"
          name="Apple Music"
          color="#FA243C"
          icon={Apple}
          connected={!!appleStatus?.authorized}
          statusText={appleStatus?.authorized ? 'Autorizado' : config?.apple?.dev_token ? 'Listo para conectar' : 'Token no configurado'}
          actions={
            appleStatus?.authorized ? (
              <button onClick={appleDisconnect} className="w-full px-3 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg text-sm flex items-center justify-center gap-1.5">
                <LogOut size={13} /> Desconectar
              </button>
            ) : (
              <button
                onClick={appleConnect}
                disabled={!config?.apple?.dev_token || loadingApple}
                className="w-full px-3 py-2 bg-[#FA243C] hover:bg-[#fb3b50] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm flex items-center justify-center gap-1.5"
              >
                {loadingApple ? <Loader2 size={13} className="animate-spin" /> : <Apple size={13} />} Conectar Apple Music
              </button>
            )
          }
        >
          {appleStatus?.authorized ? (
            <p className="text-green-400">✓ Acceso a tu biblioteca de Apple Music habilitado.</p>
          ) : config?.apple?.dev_token ? (
            <p>Autoriza con tu Apple ID para reproducir tu biblioteca de Apple Music desde el navegador.</p>
          ) : (
            <p className="text-amber-400">⚠ Falta <code className="bg-white/5 px-1 rounded">APPLE_MUSIC_DEV_TOKEN</code> en el servidor.</p>
          )}
        </ServiceCard>

        {/* ── AMAZON MUSIC ───────────────────────────────────────────── */}
        <ServiceCard
          service="amazon"
          name="Amazon Music"
          color="#25D1DA"
          icon={ShoppingBag}
          connected={amazonConnected}
          statusText={amazonConnected ? 'Marcado como conectado' : 'No vinculado'}
          actions={
            <>
              <a href="https://music.amazon.com" target="_blank" rel="noreferrer"
                 className="flex-1 px-3 py-2 bg-[#25D1DA] hover:bg-[#3ee0e8] text-black font-semibold rounded-lg text-sm flex items-center justify-center gap-1.5">
                <Play size={13} /> Abrir
              </a>
              <button onClick={amazonToggle}
                className="px-3 py-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-sm">
                {amazonConnected ? 'Olvidar' : 'Vincular'}
              </button>
            </>
          }
        >
          <p>Amazon Music no expone API pública para apps de terceros. Las búsquedas abren la app de Amazon Music directamente.</p>
        </ServiceCard>

      </div>

      {/* Modal ajustes Spotify */}
      {showSettings === 'spotify' && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 99999 }}
             onClick={() => setShowSettings(null)}>
          <div className="bg-[#0d1117] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-3">Ajustes de Spotify</h3>
            <p className="text-xs text-gray-400 mb-3">
              Si el servidor no tiene <code className="bg-white/5 px-1 rounded">SPOTIFY_CLIENT_ID</code>, puedes usar uno propio.
              Crea una app en <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noreferrer" className="text-blue-400 underline">developer.spotify.com</a>
              {' '}y agrega como Redirect URI: <code className="bg-white/5 px-1 rounded text-[10px] break-all">{REDIRECT}</code>
            </p>
            <input value={customSpotifyId} onChange={e => setCustomSpotifyId(e.target.value)}
              placeholder="Tu Client ID de Spotify"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-3 focus:outline-none focus:border-[#1DB954]" />
            <p className="text-[11px] text-gray-500 mb-3">Servidor actual: {config?.spotify?.client_id ? `✓ configurado (${config.spotify.client_id.slice(0,8)}…)` : 'no configurado'}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSettings(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancelar</button>
              <button onClick={saveCustomSpotify} className="px-4 py-2 bg-[#1DB954] text-white rounded-lg text-sm font-semibold">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajustes Apple */}
      {showSettings === 'apple' && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 99999 }}
             onClick={() => setShowSettings(null)}>
          <div className="bg-[#0d1117] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-3">Ajustes de Apple Music</h3>
            <p className="text-xs text-gray-400 mb-2">
              Apple Music requiere un <strong>Developer Token JWT</strong> firmado con tu Team ID y Key ID de Apple Developer (válido máximo 6 meses).
            </p>
            <p className="text-[11px] text-gray-500 mb-3">
              Variable: <code className="bg-white/5 px-1 rounded">APPLE_MUSIC_DEV_TOKEN</code><br/>
              Estado actual: {config?.apple?.dev_token ? '✓ configurado' : '✗ no configurado'}
            </p>
            <a href="https://developer.apple.com/documentation/applemusicapi/getting_keys_and_creating_tokens"
               target="_blank" rel="noreferrer"
               className="text-blue-400 underline text-xs">Generar token de Apple Music →</a>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowSettings(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajustes Amazon */}
      {showSettings === 'amazon' && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 99999 }}
             onClick={() => setShowSettings(null)}>
          <div className="bg-[#0d1117] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-3">Ajustes de Amazon Music</h3>
            <p className="text-sm text-gray-400 mb-3">
              Amazon Music no proporciona una API pública abierta para reproducción ni acceso a biblioteca.
              OkBot abre Amazon Music en una nueva pestaña con tu búsqueda preseleccionada.
            </p>
            <p className="text-[11px] text-gray-500">El estado "vinculado" es solo una marca local sin OAuth real.</p>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowSettings(null)} className="px-4 py-2 bg-white/5 text-gray-300 rounded-lg text-sm">Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicConnect;
